// SPDX-FileCopyrightText: 2026 Igor Brkic <igor@hyperglitch.com>
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Geometry helpers: KiCad's rotation convention and true arc extents.
 *
 * Spec §6.2 calls out arcs specifically: "use the true arc extent, not the
 * chord. (A naive endpoint bbox is wrong and will under-report.)"
 */

import { bbox, fromPoints, union, type BBox, type MaybeBBox } from './bbox.ts';

export type Point = readonly [number, number];

/**
 * KiCad's rotation, matching `RotatePoint()` in the KiCad sources:
 *
 *     x' = x·cos θ + y·sin θ
 *     y' = y·cos θ − x·sin θ
 *
 * Board coordinates have Y pointing down, so a positive angle appears
 * counter-clockwise on screen — which is what the PCB editor shows.
 */
export function rotate(x: number, y: number, angleDeg: number): Point {
  if (angleDeg === 0) return [x, y];
  const rad = (angleDeg * Math.PI) / 180;
  const c = Math.cos(rad);
  const s = Math.sin(rad);
  return [x * c + y * s, y * c - x * s];
}

/** Rotate then translate — the footprint-local to board transform. */
export function transformPoint(x: number, y: number, angleDeg: number, ox: number, oy: number): Point {
  const [rx, ry] = rotate(x, y, angleDeg);
  return [rx + ox, ry + oy];
}

/**
 * Bounding box of a rectangle rotated about the origin and translated.
 *
 * Exact: the box of the four rotated corners. Used for footprint children and
 * text, both of which are naturally rectangular in their own frame.
 */
export function rotatedBoxExtent(b: BBox, angleDeg: number, ox = 0, oy = 0): BBox {
  const corners: Point[] = [
    transformPoint(b.minX, b.minY, angleDeg, ox, oy),
    transformPoint(b.maxX, b.minY, angleDeg, ox, oy),
    transformPoint(b.maxX, b.maxY, angleDeg, ox, oy),
    transformPoint(b.minX, b.maxY, angleDeg, ox, oy),
  ];
  return fromPoints(corners) as BBox;
}

export interface Circle {
  cx: number;
  cy: number;
  r: number;
}

/**
 * Circle through three points. Returns null when they are collinear (or very
 * nearly so), which callers must treat as "this is a straight line".
 */
export function circleFrom3Points(p1: Point, p2: Point, p3: Point): Circle | null {
  const [x1, y1] = p1;
  const [x2, y2] = p2;
  const [x3, y3] = p3;
  const d = 2 * (x1 * (y2 - y3) + x2 * (y3 - y1) + x3 * (y1 - y2));
  if (Math.abs(d) < 1e-12) return null;
  const s1 = x1 * x1 + y1 * y1;
  const s2 = x2 * x2 + y2 * y2;
  const s3 = x3 * x3 + y3 * y3;
  const cx = (s1 * (y2 - y3) + s2 * (y3 - y1) + s3 * (y1 - y2)) / d;
  const cy = (s1 * (x3 - x2) + s2 * (x1 - x3) + s3 * (x2 - x1)) / d;
  const r = Math.hypot(x1 - cx, y1 - cy);
  if (!Number.isFinite(r) || r > 1e7) return null; // degenerate: treat as a line
  return { cx, cy, r };
}

/** Normalise to [0, 2π). */
function norm2pi(a: number): number {
  const t = a % (2 * Math.PI);
  return t < 0 ? t + 2 * Math.PI : t;
}

/**
 * True extent of the arc start→mid→end.
 *
 * The four axis extremes (0°, 90°, 180°, 270°) are included only when they fall
 * inside the swept angle; the endpoints are always included. Collinear input
 * degrades to the box of the three points, which is correct for a straight line
 * and conservative for a very flat arc.
 */
export function arcExtent(start: Point, mid: Point, end: Point): BBox {
  const endpoints = fromPoints([start, mid, end]) as BBox;
  const circle = circleFrom3Points(start, mid, end);
  if (circle === null) return endpoints;

  const { cx, cy, r } = circle;
  const aStart = norm2pi(Math.atan2(start[1] - cy, start[0] - cx));
  const aMid = norm2pi(Math.atan2(mid[1] - cy, mid[0] - cx));
  const aEnd = norm2pi(Math.atan2(end[1] - cy, end[0] - cx));

  // Sweep counter-clockwise (increasing angle) from start; the arc goes that
  // way if `mid` is reached before `end` does.
  const ccwMid = norm2pi(aMid - aStart);
  const ccwEnd = norm2pi(aEnd - aStart);
  const counterClockwise = ccwMid <= ccwEnd;
  const sweep = counterClockwise ? ccwEnd : 2 * Math.PI - ccwEnd;

  const withinSweep = (angle: number): boolean => {
    const delta = counterClockwise ? norm2pi(angle - aStart) : norm2pi(aStart - angle);
    return delta <= sweep + 1e-9;
  };

  let out = endpoints;
  for (let k = 0; k < 4; k++) {
    const angle = (k * Math.PI) / 2;
    if (!withinSweep(angle)) continue;
    const px = cx + r * Math.cos(angle);
    const py = cy + r * Math.sin(angle);
    out = union(out, bbox(px, py, px, py)) as BBox;
  }
  return out;
}

/**
 * Extent of an arc given centre, a start point and a sweep angle — the
 * `(gr_arc (start centre) (mid …) (end …))` variants all normalise to
 * {@link arcExtent}; this form covers `(fp_arc)` written centre-first in some
 * older files. Conservative: falls back to the full circle if the sweep cannot
 * be determined.
 */
export function circleExtent(cx: number, cy: number, r: number): BBox {
  return bbox(cx - r, cy - r, cx + r, cy + r);
}

/**
 * A cubic Bézier lies entirely within the convex hull of its control points, so
 * the box of the control points is always conservative. Used for `gr_curve`.
 */
export function bezierExtent(points: ReadonlyArray<Point>): MaybeBBox {
  return fromPoints(points);
}

export interface ArcParams {
  cx: number;
  cy: number;
  r: number;
  /** Start angle, in canvas convention (y-down, radians). */
  a0: number;
  a1: number;
  /** Pass to `ctx.arc()` as its `counterclockwise` argument. */
  counterclockwise: boolean;
}

/**
 * Centre, radius and swept angles of the arc start→mid→end, in the form
 * `CanvasRenderingContext2D.arc()` wants. Null when the points are collinear,
 * which the caller should draw as a straight line.
 */
export function arcParams(start: Point, mid: Point, end: Point): ArcParams | null {
  const circle = circleFrom3Points(start, mid, end);
  if (circle === null) return null;
  const { cx, cy, r } = circle;

  const norm = (a: number): number => {
    const t = a % (2 * Math.PI);
    return t < 0 ? t + 2 * Math.PI : t;
  };
  const a0 = Math.atan2(start[1] - cy, start[0] - cx);
  const a1 = Math.atan2(end[1] - cy, end[0] - cx);
  const aMid = Math.atan2(mid[1] - cy, mid[0] - cx);

  // Does the sweep from start to end pass through mid with increasing angle?
  const increasing = norm(aMid - a0) <= norm(a1 - a0);
  return { cx, cy, r, a0, a1, counterclockwise: !increasing };
}
